/**
 * Detect body weight updates from chat messages
 */

// Patterns for detecting body weight mentions
const WEIGHT_PATTERNS = [
  // "I weigh 199 lbs" / "I weigh 199 lbs now" / "I'm 199 lbs now" / "I am 199 pounds"
  /\bi\s+weigh\s+(\d{2,4})\s*(?:lbs?|pounds?|kg)?/gi,
  
  // "I'm 199 lbs" / "I am 199 lbs"
  /\bi(?:'m|\s+am)\s+(\d{2,4})\s*(?:lbs?|pounds?|kg)/gi,
  
  // "my weight is 199" / "my current weight is 199"
  /\bmy\s+(?:current\s+)?weight\s+is\s+(\d{2,4})\s*(?:lbs?|pounds?|kg)?/gi,
  
  // "199 lbs now" / "down to 199" / "at 199 lbs"
  /\b(?:(?:down|up)\s+to|(?:at|hit|reached)\s+)(\d{2,4})\s*(?:lbs?|pounds?|kg)?/gi,
  
  // "currently 199 lbs" / "sitting at 199"
  /\b(?:currently|sitting\s+at|weighing\s+in\s+at)\s+(\d{2,4})\s*(?:lbs?|pounds?|kg)?/gi,
  
  // "lost/gained X lbs, now 199" - capture the new weight
  /\b(?:lost|gained|dropped)\s+\d+\s*(?:lbs?|pounds?|kg)?,?\s*(?:now|at|down\s+to|up\s+to)\s+(\d{2,4})\s*(?:lbs?|pounds?|kg)?/gi,
  
  // "I'm at 199" / "weight is 199"
  /\b(?:i'?m\s+at|weight\s+is)\s+(\d{2,4})(?:\s*(?:lbs?|pounds?|kg))?/gi,
];

// Words that indicate we're talking about body weight, not exercise weight
const BODY_WEIGHT_INDICATORS = [
  'weigh', 'weight', 'body', 'bodyweight', 'bw', 'scale', 'lost', 'gained', 
  'dropped', 'down to', 'up to', 'currently', 'sitting at', 'i am', "i'm"
];

// Words that indicate we're NOT talking about body weight
const EXERCISE_WEIGHT_INDICATORS = [
  'bench', 'squat', 'deadlift', 'press', 'row', 'curl', 'lift', 'pr', 'rep', 
  'set', 'exercise', 'barbell', 'dumbbell', 'plate', 'max', '1rm', 'orm'
];

export interface DetectedWeight {
  weight: number;
  confidence: 'high' | 'medium' | 'low';
}

/**
 * Detect body weight updates from a chat message
 */
export function detectWeightFromMessage(message: string): DetectedWeight | null {
  const lower = message.toLowerCase();
  
  // Check if message is likely about exercise weights, not body weight
  const hasExerciseIndicators = EXERCISE_WEIGHT_INDICATORS.some(ind => lower.includes(ind));
  const hasBodyWeightIndicators = BODY_WEIGHT_INDICATORS.some(ind => lower.includes(ind));
  
  // If it has exercise indicators but no body weight indicators, skip
  if (hasExerciseIndicators && !hasBodyWeightIndicators) {
    return null;
  }
  
  // Try each pattern
  for (const pattern of WEIGHT_PATTERNS) {
    pattern.lastIndex = 0;
    const match = pattern.exec(message);
    
    if (match && match[1]) {
      const weight = parseInt(match[1]);
      
      // Validate weight is reasonable for body weight (50-500 lbs)
      if (weight >= 50 && weight <= 500) {
        // Determine confidence based on context
        let confidence: 'high' | 'medium' | 'low' = 'medium';
        
        if (hasBodyWeightIndicators && !hasExerciseIndicators) {
          confidence = 'high';
        } else if (hasExerciseIndicators) {
          confidence = 'low';
        }
        
        return { weight, confidence };
      }
    }
  }
  
  return null;
}

/**
 * Check if the detected weight is different from current weight
 */
export function isWeightChanged(
  detected: DetectedWeight, 
  currentWeight: number | undefined
): boolean {
  if (!currentWeight) return true;
  return detected.weight !== currentWeight;
}
